import React, { useEffect, useRef, useState } from 'react';

interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  value: number;
  /** Number of decimals to display when the input is not being edited. */
  decimals?: number;
  /** Minimum allowed numeric value. Enforced on commit (blur / Enter). */
  min?: number;
  /** Maximum allowed numeric value. Enforced on commit (blur / Enter). */
  max?: number;
  /** Called with a parsed numeric value while the user types a valid number. */
  onValueChange?: (value: number) => void;
  /** Called when the user commits a value (blur or Enter). */
  onCommit?: (value: number) => void;
}

/**
 * Standard text input that only accepts numeric input (digits, optional sign and decimal separator).
 *
 * Behavior:
 *  - Uses `type="text"` with `inputMode="decimal"` to avoid the quirks of the native number input
 *    (where keys like 8/9 might step values, scroll wheel changes values, and backspace can behave
 *    inconsistently with formatted values).
 *  - Blocks letter input entirely.
 *  - Maintains its own local string state so users can clear the field and use backspace freely.
 *  - Reports parsed numeric value via `onValueChange` while typing (when the current text parses to
 *    a valid number), and via `onCommit` on blur / Enter (with min/max clamping).
 */
export const NumericInput: React.FC<NumericInputProps> = ({
  value,
  decimals = 2,
  min,
  max,
  onValueChange,
  onCommit,
  onBlur,
  onKeyDown,
  ...rest
}) => {
  const format = (v: number) => (Number.isFinite(v) ? v.toFixed(decimals) : '');
  const [text, setText] = useState<string>(() => format(value));
  const isFocusedRef = useRef(false);

  // Keep local text in sync with external value when the input is not being edited.
  useEffect(() => {
    if (!isFocusedRef.current) {
      setText(format(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals]);

  // Accepts an optional leading minus, digits, and a single decimal separator.
  const NUMERIC_RE = /^-?\d*([.,]\d*)?$/;

  const parse = (s: string): number => {
    const normalized = s.replace(',', '.');
    return parseFloat(normalized);
  };

  const clamp = (v: number): number => {
    let r = v;
    if (typeof min === 'number' && r < min) r = min;
    if (typeof max === 'number' && r > max) r = max;
    return r;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (next !== '' && !NUMERIC_RE.test(next)) {
      // Reject any non-numeric characters (letters, multiple dots, etc.).
      return;
    }
    setText(next);
    const parsed = parse(next);
    if (!isNaN(parsed) && onValueChange) {
      onValueChange(parsed);
    }
  };

  const commit = () => {
    const parsed = parse(text);
    if (isNaN(parsed)) {
      // Restore previous valid value.
      setText(format(value));
      return;
    }
    const clamped = clamp(parsed);
    setText(format(clamped));
    if (onCommit) onCommit(clamped);
    else if (onValueChange && clamped !== parsed) onValueChange(clamped);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    commit();
    if (onBlur) onBlur(e);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commit();
      (e.currentTarget as HTMLInputElement).blur();
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
    />
  );
};
